import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Collapse, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  useCalculateSolarEstimationCostAdminMutation,
  useCalculateSolarEstimationCostMutation,
  useLazyViewSolarEstimationAdminQuery,
  useLazyViewSolarEstimationQuery,
  usePersistEstimationMutation,
  usePurchaseSolarEstimationMutation,
  useSubmitForApprovalMutation,
  useUpdateEstimationMutation,
} from "../../../../redux/api/solar-estimation/solarEstimationAPI";
import { setBillCalculatorFiltersSpaceAndSpaceDetails } from "../../../../redux/features/bill-calculator-filter/bill-calculator-filter-slice";
import { setBillingSpaceFilter } from "../../../../redux/features/billing-space-filter/billing-space-filter-slice";
import {
  selectCountry,
  selectProfile,
} from "../../../../redux/features/user/user-slice";
import CalculateSolarEstimationCostRequestDTO from "../../../../shared/oversight-core/dtos/request-dtos/calculate-solar-estimation-cost-request-dto";
import SubmitForApprovalRequestDTO from "../../../../shared/oversight-core/dtos/request-dtos/submit-for-approval-request-dto";
import CalculateSolarEstimationCostResponseDTO from "../../../../shared/oversight-core/dtos/response-dtos/calculate-solar-estimation-cost-response-dto";
import {
  IViewSolarEstimationData,
  IViewSolarEstimationOutputParams,
  ViewSolarEstimationResponseDTO,
} from "../../../../shared/oversight-core/dtos/response-dtos/view-solar-estimation-response-dto";
import { EApplicationType } from "../../../../shared/oversight-core/enums/application-type";
import { EDeviceTypes } from "../../../../shared/oversight-core/enums/device-types";
import { EstimationStatus } from "../../../../shared/oversight-core/enums/estimation-status";
import { EEstimationType } from "../../../../shared/oversight-core/enums/estimation-type";
import { EInverterPhaseType } from "../../../../shared/oversight-core/enums/inverter-phase-type";
import { OvstErrorCode } from "../../../../shared/oversight-core/enums/ovst-error-codes";
import { ESolarSolutionType } from "../../../../shared/oversight-core/enums/sola-solution-type";
import { ESolarSimLocalStorage } from "../../../../shared/oversight-core/enums/solar-sim-local-storage";
import { ESolarSystemType } from "../../../../shared/oversight-core/enums/solar-system-type ";
import { UserRole } from "../../../../shared/oversight-core/enums/user-roles";
import { EUtilityOptionType } from "../../../../shared/oversight-core/enums/utility-option-type";
import { EViewSolarEstimationSolarProductCategory } from "../../../../shared/oversight-core/enums/view-solar-estimation-solar-product-category";
import { AppRoute } from "../../../../shared/oversight-core/interfaces/app-routes";
import { IHttpError } from "../../../../shared/oversight-core/interfaces/http-errror";
import { IViewSolarEstimationSolarProductAndOtherDetails } from "../../../../shared/oversight-core/interfaces/view-solar-estimation-solar-system";
import AppDropDown from "../../../../shared/oversight-core/ui-elements/app-dropdown/app-drop-down";
import { Option } from "../../../../shared/oversight-core/ui-elements/app-select/app-select";
import AppSwitch from "../../../../shared/oversight-core/ui-elements/app-switch/app-switch";
import ArrowButton from "../../../../shared/oversight-core/ui-elements/arrow-button/arrow-button";
import AppButton from "../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import SpinnerModal from "../../../../shared/oversight-core/ui-elements/spinner/spinner";
import StepsNumber, {
  ISteps,
} from "../../../../shared/oversight-core/ui-elements/steps-number/steps-number";
import {
  getStatusColor,
  getStatusNameUser,
} from "../../../../shared/oversight-core/utils/estimation-status";
import { filterByCategories } from "../../../../shared/oversight-core/utils/filter-by-categories";
import { formatWithCommas } from "../../../../shared/oversight-core/utils/format-with-commas";
import Helper from "../../../../shared/oversight-core/utils/helpers";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../shared/oversight-core/utils/toast";
import AddUsageInfo from "../../../build-system/components/add-usage-info/add-usage-info";
import ApprovedSolarComponent from "../../../build-system/components/approved-solar-component/approved-solar-component";
import SelectedSolarSystemCard from "../../../build-system/components/selected-solar-system-card/selected-solar-system-card";
import SelectedWaterHeaterSystemCard from "../../../build-system/components/selected-water-heater-system-card/selected-water-heater-system-card";
import { ISimplifiedDevice } from "../../../build-system/components/simplified-device-energy-card/simplified-device-energy-card";
import SubmitForExpertApprovalModal from "../../../build-system/components/submit-for-expert-approval-modal/submit-for-expert-approval-modal";
import BillCalculator from "../../../dashboard/bill-calculator/bill-calculator";
import ApproveSystemModal from "../components/approve-system-modal/approve-system-modal";
import PurchaseHistoryModal from "../components/purchase-history-modal/purchase-history-modal";
import SiteLocationCard from "../components/site-location-card/site-location-card";
import UpdateHistoryModal from "../components/update-history-modal/update-history-modal";
import styles from "./view-estimation.module.scss";

interface IEstimationInfo {
  estimationNo: number;
  createdBy: string;
  userId: string;
  date: string;
  email: string;
  title: string;
  currency: string;
  mobileNumber: string;
  estimationStatus: string;
  address: {
    latitude: number;
    locationName: string;
    longitude: string;
    url: string;
  } | null;
  isDraft: boolean;
}

const solarSystemTypesOption: Option[] = [
  {
    value: ESolarSystemType.BACKUP,
    label: "Backup",
  },
  {
    value: ESolarSystemType.OFF_GRID,
    label: "Off-Grid",
  },
  {
    value: ESolarSystemType.HYBRID,
    label: "Hybrid",
  },
];

const inverterPhaseTypeOptions: Option[] = [
  {
    value: EInverterPhaseType.SINGLE_PHASE,
    label: "1 Phase",
  },
  {
    value: EInverterPhaseType.THREE_PHASE,
    label: "3 Phase",
  },
  {
    value: EInverterPhaseType.NONE,
    label: "None",
  },
];

const applicationTypeOptions: Option[] = [
  {
    value: EApplicationType.RESIDENTIAL,
    label: "Residential",
  },
  {
    value: EApplicationType.HOTELS,
    label: "Hotels",
  },
  {
    value: EApplicationType.HOSTELS,
    label: "Hostels",
  },
  {
    value: EApplicationType.SCHOOLS,
    label: "Schools",
  },
];

const utilityOptions: Option[] = [
  {
    value: EUtilityOptionType.SHOWER,
    label: "Shower",
  },
  {
    value: EUtilityOptionType.BATHTUB_JACUZZI,
    label: "Bathtub/Jacuzzi",
  },
];

const systemVoltageOptions: Option[] = [
  {
    value: "12",
    label: "12V",
  },
  {
    value: "24",
    label: "24V",
  },
  {
    value: "48",
    label: "48V",
  },
];

const ViewEstimation = () => {
  const profile = useSelector(selectProfile);
  const country = useSelector(selectCountry);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const pathname = location.pathname;
  const estimationId = location.state?.estimationId;
  const isUserView = location.state?.isUserView;

  const [selectedStep, setSelectedStep] = useState(2);
  const [showUpdateHistoryModal, setShowUpdateHistoryModal] = useState(false);
  const [estimationInfo, setEstimationInfo] = useState<IEstimationInfo>({
    estimationNo: 0,
    createdBy: "",
    userId: "",
    date: "",
    email: "",
    title: "",
    currency: "",
    mobileNumber: "",
    estimationStatus: "",
    address: null,
    isDraft: false,
  });
  const [monthlyEnergyNeed, setMonthlyEnergyNeed] = useState(0);
  const [maxPower, setMaxPower] = useState(0);
  const [dischargeRate, setDischargeRate] = useState(0);
  const [nightLoad, setNightLoad] = useState(0);
  const [sunlightHours, setSunlightHours] = useState(5);
  const [waterDemand, setWaterDemand] = useState(0);
  const [assembleSystemData, setAssembleSystemData] = useState<
    IViewSolarEstimationData | undefined
  >(undefined);
  const [selectedSystemOutput, setSelectedSystemOutput] =
    useState<IViewSolarEstimationOutputParams>({
      totalWattHours: 0,
      totalCost: 0,
      systemVoltage: 0,
      totalAmpHours: 0,
      nightLoadAmpHours: 0,
      batteryAmpHours: 0,
      maxPowerInWatt: 0,
      grandTotal: 0,
      totalAccessoriesCost: 0,
      laborCost: 0,
      totalLiters: 0,
    });
  const [collapsed, setCollapsed] = useState(false);
  const [waterHeaterCollapsed, setWaterHeaterCollapsed] = useState(false);
  const [includeOtherFees, setIncludeOtherFees] = useState(false);
  const [componentCost, setComponentCost] = useState(0);
  const [installationCost, setInstallationCost] = useState(0);
  const [accessoriesCost, setAccessoriesCost] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [currency, setCurrency] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<Option>({
    value: EstimationStatus.APPROVED,
    label: "Approve",
  });
  const [showApproveSystemModal, setShowApproveSystemModal] = useState(false);
  const [solarEstimationType, setSolarEstimationType] =
    useState<EEstimationType>();
  const [devices, setDevices] = useState<ISimplifiedDevice[]>([]);
  const [userSelected, setUserSelected] = useState<
    IViewSolarEstimationSolarProductAndOtherDetails[]
  >([]);
  const [comment, setComment] = useState("");
  const [
    isShowSubmitForExpertApprovalModal,
    setIsShowSubmitForExpertApprovalModal,
  ] = useState(false);
  const [showPurchaseHistoryModal, setShowPurchaseHistoryModal] =
    useState(false);
  const [solarSystemType, setSolarSystemType] = useState<Option>({
    ...solarSystemTypesOption[2],
  });
  const [inverterPhaseType, setInverterPhaseType] = useState<Option>({
    ...inverterPhaseTypeOptions[0],
  });
  const [applicationType, setApplicationType] = useState<Option>({
    ...applicationTypeOptions[0],
  });
  const [solarSolutionType, setSolarSolutionType] =
    useState<ESolarSolutionType>(ESolarSolutionType.BOTH);
  const [utilityOption, setUtilityOption] = useState<Option>({
    ...utilityOptions[0],
  });
  const [numberOfUsers, setNumberOfUsers] = useState(0);
  const [isUpdateCurrentState, setIsUpdateCurrentSate] = useState(false);
  const [demandPerUser, setDemandPerUser] = useState<number | null>(null);
  const [autonomyDays, setAutonomyDays] = useState<number | null>(null);
  const [systemVoltage, setSystemVoltage] = useState<Option | null>(null);

  const [
    triggerViewSolarEstimationAdmin,
    { isFetching: isFetchingViewSolarEstimationAdmin },
  ] = useLazyViewSolarEstimationAdminQuery();
  const [triggerViewSolarEstimation, { isFetching: isFetchingViewEstimation }] =
    useLazyViewSolarEstimationQuery();
  const [submitForApproval, { isLoading: isSubmittingForApproval }] =
    useSubmitForApprovalMutation();
  const [
    triggerCalculateSolarEstimationCost,
    { isLoading: isLoadingCalculateSolarEstimationCost },
  ] = useCalculateSolarEstimationCostMutation();
  const [
    triggerCalculateSolarEstimationCostAdmin,
    { isLoading: isLoadingCalculateSolarEstimationCostAdmin },
  ] = useCalculateSolarEstimationCostAdminMutation();
  const [triggerPersistEstimation, { isLoading: isLoadingPersistEstimation }] =
    usePersistEstimationMutation();
  const [triggerUpdateEstimation, { isLoading: isLoadingUpdateEstimation }] =
    useUpdateEstimationMutation();
  const [
    triggerPurchaseSolarEstimation,
    { isLoading: isLoadingPurchaseSolarEstimation },
  ] = usePurchaseSolarEstimationMutation();

  useEffect(() => {
    if (estimationId && profile?.role) {
      const triggerViewEstimation =
        profile?.role === UserRole.ROLE_SOLAR_DASHBOARD_ADMIN
          ? triggerViewSolarEstimationAdmin
          : triggerViewSolarEstimation;

      triggerViewEstimation({ estimationId })
        .unwrap()
        .then((res: ViewSolarEstimationResponseDTO) => {
          setComment(
            profile?.role === UserRole.ROLE_SOLAR_DASHBOARD_ADMIN
              ? res.estimationView.userComment || ""
              : res.estimationView.adminComment || "",
          );
          setSolarEstimationType(res.estimationView.solarEstimationType);
          setMonthlyEnergyNeed(
            Number(res.estimationView.data.inputParams.monthlyRequiredEnergy),
          );
          setMaxPower(
            Number(res.estimationView.data.inputParams.maxRequiredPower),
          );
          setDischargeRate(
            Number(res.estimationView.data.inputParams.dischargeRatePercentage),
          );
          setNightLoad(
            Number(res.estimationView.data.inputParams.nightLoadPercentage),
          );
          setSunlightHours(
            Number(res.estimationView.data.inputParams.sunlightHours),
          );
          setWaterDemand(
            Number(res.estimationView.data.inputParams.waterDemand),
          );
          setNumberOfUsers(
            Number(res.estimationView.data.inputParams.numberOfUsers),
          );
          setAutonomyDays(
            Number(res.estimationView.data.inputParams.autonomyDays),
          );
          setDemandPerUser(
            Number(res.estimationView.data.inputParams.demandPerUser),
          );
          setSolarSolutionType(res.estimationView.estimationType);
          setSelectedSystemOutput(
            res.estimationView.data.outputParams || {
              totalWattHours: 0,
              totalCost: 0,
              systemVoltage: 0,
              totalAmpHours: 0,
              nightLoadAmpHours: 0,
              batteryAmpHours: 0,
              maxPowerInWatt: 0,
              grandTotal: 0,
              totalAccessoriesCost: 0,
              laborCost: 0,
              totalLiters: 0,
            },
          );
          setEstimationInfo({
            estimationNo: res.estimationView.estimationId,
            createdBy:
              res.estimationView.user.firstName +
              " " +
              res.estimationView.user.lastName,
            userId: res.estimationView.user.id,
            date: res.estimationView.createdAt,
            email: res.estimationView.user.email,
            title: res.estimationView.title || "",
            currency: res.estimationView.currency,
            mobileNumber: res.estimationView.user.contactNumber,
            estimationStatus: res.estimationView.status ?? "",
            address: res.estimationView.address,
            isDraft: res.estimationView.isDraft,
          });
          setAssembleSystemData(res.estimationView.data);
          setCurrency(res.estimationView.currency);
          setIncludeOtherFees(res.estimationView.installationCostRequested);
          if (res.estimationView.basicSchedule) {
            setDevices(
              res.estimationView.basicSchedule.powerConsumerUsages.map((i) => {
                return {
                  deviceType: i.type as EDeviceTypes,
                  deviceName: i.name,
                  amount: String(i.amount),
                  wattage: String(i.wattage),
                  usage: String(i.usageInHours),
                  id: uuidv4(),
                  units: 0,
                  maxPower: 0,
                };
              }),
            );
          }

          setAccessoriesCost(
            res.estimationView.data.outputParams?.totalAccessoriesCost || 0,
          );

          setInstallationCost(
            res.estimationView.data.outputParams?.laborCost || 0,
          );

          if (res.estimationView.data.outputParams?.grandTotal) {
            setTotalCost(res.estimationView.data.outputParams?.grandTotal);
          }

          if (res.estimationView.spaceCluster) {
            const selectedSpaceCluster = res.estimationView.spaceCluster;

            const { id: clusterId, rootSpace } = selectedSpaceCluster;

            const selectedSpaceDetails = {
              spaceClusterId: clusterId,
              spaceId: rootSpace.id,
            };

            dispatch(setBillingSpaceFilter({ ...selectedSpaceCluster }));
            dispatch(
              setBillCalculatorFiltersSpaceAndSpaceDetails({
                selectedSpace: { ...rootSpace, clusterId },
                selectedSpaceDetails,
              }),
            );
          }

          const selectedSolarTypeOption = solarSystemTypesOption.find(
            (s) => s.value === res.estimationView.data.inputParams.systemType,
          );

          const selectedSystemVoltageOption = systemVoltageOptions.find(
            (s) =>
              Number(s.value) ===
              res.estimationView.data.inputParams.systemVoltage,
          );

          const selectedInverterPhaseTypeOption = inverterPhaseTypeOptions.find(
            (i) => i.value === res.estimationView.inverterPhaseType,
          );

          const selectedUtilityOption = utilityOptions.find(
            (u) => u.value === res.estimationView.utilityOptionType,
          );

          const selectedApplicationType = applicationTypeOptions.find(
            (a) => a.value === res.estimationView.applicationType,
          );

          if (selectedSolarTypeOption) {
            setSolarSystemType({
              value: selectedSolarTypeOption.value,
              label: selectedSolarTypeOption.label,
            });
          }

          if (selectedSystemVoltageOption) {
            setSystemVoltage({
              value: selectedSystemVoltageOption.value,
              label: selectedSystemVoltageOption.label,
            });
          }

          if (selectedInverterPhaseTypeOption) {
            setInverterPhaseType({
              value: selectedInverterPhaseTypeOption.value,
              label: selectedInverterPhaseTypeOption.label,
            });
          }

          if (selectedApplicationType) {
            setApplicationType({
              value: selectedApplicationType.value,
              label: selectedApplicationType.label,
            });
          }

          if (selectedUtilityOption) {
            setUtilityOption({
              value: selectedUtilityOption.value,
              label: selectedUtilityOption.label,
            });
          }
        })
        .catch(() => {
          setSelectedSystemOutput({
            totalWattHours: 0,
            totalCost: 0,
            systemVoltage: 0,
            totalAmpHours: 0,
            nightLoadAmpHours: 0,
            batteryAmpHours: 0,
            maxPowerInWatt: 0,
            grandTotal: 0,
            totalAccessoriesCost: 0,
            laborCost: 0,
            totalLiters: 0,
          });
          setCurrency("");
        });
    }
  }, [
    triggerViewSolarEstimationAdmin,
    triggerViewSolarEstimation,
    estimationId,
    profile?.role,
    isUpdateCurrentState,
  ]);

  useEffect(() => {
    if (estimationId && profile?.role) {
      const requestBody: CalculateSolarEstimationCostRequestDTO = {
        estimationId: String(estimationId),
        installationCostRequired: includeOtherFees,
      };

      if (estimationInfo.address) {
        requestBody.address = estimationInfo.address.locationName;
        requestBody.longitude = Number(estimationInfo.address.longitude);
        requestBody.latitude = Number(estimationInfo.address.latitude);
      }

      (profile?.role === UserRole.ROLE_SOLAR_DASHBOARD_ADMIN
        ? triggerCalculateSolarEstimationCostAdmin
        : triggerCalculateSolarEstimationCost)({
        ...requestBody,
      })
        .unwrap()
        .then((res: CalculateSolarEstimationCostResponseDTO) => {
          setComponentCost(res.equipmentCost);
          setTotalCost(res.grandTotal);
          if (includeOtherFees) {
            setInstallationCost(res.totalLaborCost);
            setAccessoriesCost(res.totalAccessoriesCost);
          }
        })
        .catch(() => {
          showErrorMessage("Address is invalid. Please enter correct address");
        });
    }
  }, [
    triggerCalculateSolarEstimationCost,
    triggerCalculateSolarEstimationCostAdmin,
    estimationInfo.address,
    includeOtherFees,
    profile?.role,
  ]);

  useEffect(() => {
    if (!assembleSystemData) return;

    let selectedProducts;

    if (
      assembleSystemData.adminSelectedProducts &&
      assembleSystemData.adminSelectedProducts.length > 0
    ) {
      selectedProducts = assembleSystemData.adminSelectedProducts;
    } else {
      selectedProducts = assembleSystemData.userSelectedProducts ?? [];
    }

    const recommendedProducts = assembleSystemData.recommendedProducts;

    const mappedSelectedProducts =
      selectedProducts?.map((usp) => {
        let productDetails:
          | IViewSolarEstimationSolarProductAndOtherDetails
          | undefined;

        switch (usp.category) {
          case EViewSolarEstimationSolarProductCategory.INVERTERS:
            productDetails = recommendedProducts?.INVERTERS?.find(
              (inverter) => inverter.product.id === usp.id,
            );
            break;
          case EViewSolarEstimationSolarProductCategory.BATTERIES:
            productDetails = recommendedProducts?.BATTERIES?.find(
              (battery) => battery.product.id === usp.id,
            );
            break;
          case EViewSolarEstimationSolarProductCategory.PANELS:
            productDetails = recommendedProducts?.PANELS?.find(
              (panel) => panel.product.id === usp.id,
            );
            break;
          case EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS:
            productDetails = recommendedProducts?.CHARGE_CONTROLLERS?.find(
              (controller) => controller.product.id === usp.id,
            );
            break;
          case EViewSolarEstimationSolarProductCategory.WATER_HEATERS:
            productDetails = recommendedProducts?.WATER_HEATERS?.find(
              (waterHeater) => waterHeater.product.id === usp.id,
            );
            break;
          case EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS:
            productDetails =
              recommendedProducts?.WATER_HEATER_CONTROLLERS?.find(
                (controller) => controller.product.id === usp.id,
              );
            break;
        }

        return productDetails
          ? { ...productDetails, count: usp.count, isSelected: usp.isSelected }
          : null;
      }) ?? [];

    setUserSelected(
      mappedSelectedProducts.filter(
        (item): item is IViewSolarEstimationSolarProductAndOtherDetails =>
          item !== null,
      ),
    );
  }, [assembleSystemData, profile?.role]);

  const steps: ISteps[] = [
    {
      id: 1,
      stepName: "Add Usage Info",
      stepIcon: "autoplay",
    },
    {
      id: 2,
      stepName: "Assemble System",
      stepIcon: "widgets",
    },
  ];

  const onSubmitForApproval = (
    address: string,
    longitude: number,
    latitude: number,
    note: string,
  ) => {
    if (estimationId) {
      const requestBody: SubmitForApprovalRequestDTO = {
        estimationId: estimationId,
        includeInstallationCost: includeOtherFees,
        longitude,
        latitude,
        address,
        note,
      };

      submitForApproval({ ...requestBody })
        .unwrap()
        .then(() => {
          if (pathname.includes("advanced")) {
            localStorage.removeItem(
              ESolarSimLocalStorage.ESTIMATOR_DETAILS_ADVANCED,
            );
          }

          if (pathname.includes("simplified")) {
            localStorage.removeItem(
              ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
            );
            localStorage.removeItem("simplifiedSave");
          }

          showSuccessMessage("Submitted for approval successfully");
          setIsShowSubmitForExpertApprovalModal(false);
          navigate(AppRoute.ESTIMATIONS);
        })
        .catch(() => {
          showErrorMessage("Failed to submit for approval");
        });
    }
  };

  const onPurchaseSystem = (estimationId: string) => {
    // const base = "https://priyanthab5.wixstudio.com/shop";
    // const cart = (assembleSystemData?.userSelectedProducts ?? []).map(
    //   (usp) => ({
    //     id: usp.id,
    //     count: usp.count,
    //   })
    // );

    // const url = new URL(base);

    // url.searchParams.set(
    //   "products",
    //   cart.map((item) => `${item.id}:${item.count}`).join(",")
    // );

    // const finalUrl = url.toString();

    // window.open(finalUrl, "_blank", "noopener,noreferrer");

    // if (pathname.includes("advanced")) {
    //   localStorage.removeItem(ESolarSimLocalStorage.ESTIMATOR_DETAILS_ADVANCED);
    // }

    // if (pathname.includes("simplified")) {
    //   localStorage.removeItem(
    //     ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED
    //   );
    //   localStorage.removeItem("simplifiedSave");
    // }

    triggerPurchaseSolarEstimation({ estimationId })
      .unwrap()
      .then(() => {
        showSuccessMessage("System purchased successfully");
        navigate(AppRoute.ESTIMATIONS);
      })
      .catch((error: IHttpError) => {
        if (
          error.status === 412 &&
          error.ovstErrorCode === OvstErrorCode.OVST_CONS_0001
        ) {
          showErrorMessage("Solar estimation is yet to be reviewed.");
          return;
        }
        showErrorMessage("Failed to purchase system");
      });
  };

  const getHeaderName = (status: EstimationStatus) => {
    switch (status) {
      case EstimationStatus.REJECTED:
        return "Rejected System";
      case EstimationStatus.APPROVED:
        return "Approved System";
      case EstimationStatus.APPROVED_WITH_CHANGES:
        return "Approved System";
      case EstimationStatus.PENDING:
        return "Drafted System";
      case EstimationStatus.SUBMITTED:
        return "Awaiting Approval";
      case EstimationStatus.MODIFIED:
        return "Awaiting Approval";
      case EstimationStatus.PURCHASED:
        return "Purchased System";
      default:
        return "";
    }
  };

  const onSave = async () => {
    if (estimationId) {
      await triggerViewSolarEstimation({ estimationId })
        .unwrap()
        .then((viewRes) => {
          const estimationView = viewRes.estimationView;
          const inputParams = estimationView.data.inputParams;

          if (
            viewRes.estimationView.solarEstimationType ===
            EEstimationType.SIMPLIFIED
          ) {
            triggerPersistEstimation({
              estimationId,
              estimationToken: "",
              title: estimationView.title || "",
              usages: estimationView.basicSchedule?.powerConsumerUsages || [],
              isDraft: true,
              countryCode: estimationView.countryCode || country,
            })
              .unwrap()
              .then((persistRes) => {
                triggerUpdateEstimation({
                  estimationId,
                  estimatorToken: "",
                  title: estimationView.title || "",
                  estimationType: estimationView.estimationType,
                  applicationType: estimationView.applicationType,
                  utilityOptionType: estimationView.utilityOptionType,
                  solarEstimationType: EEstimationType.SIMPLIFIED,
                  systemType: estimationView.systemType,
                  inverterPhaseType: estimationView.inverterPhaseType,
                  monthlyRequiredEnergy: inputParams.monthlyRequiredEnergy || 0,
                  maxRequiredPower: inputParams.maxRequiredPower || 0,
                  numberOfUsers: inputParams.numberOfUsers,
                  demandPerUser: inputParams.demandPerUser,
                  nightLoad: inputParams.nightLoadPercentage || 0,
                  sunlightHours: inputParams.sunlightHours || 0,
                  autonomyDays: inputParams.autonomyDays || null,
                  systemVoltage: inputParams.systemVoltage || null,
                  estimationSpaceClusterId: null,
                  estimationBasicScheduleId: persistRes.scheduleId,
                  isDraft: false,
                  countryCode: estimationView.countryCode,
                  installationCostRequested: includeOtherFees,
                })
                  .unwrap()
                  .then(() => {
                    showSuccessMessage("Estimation saved successfully");
                  })
                  .catch(() => {
                    showErrorMessage("Failed to save estimation");
                  });
              })
              .catch(() => {
                showErrorMessage("Failed to save estimation");
              });

            return;
          }

          triggerUpdateEstimation({
            estimationId,
            estimatorToken: "",
            title: estimationView.title || "",
            estimationType: estimationView.estimationType,
            applicationType: estimationView.applicationType,
            utilityOptionType: estimationView.utilityOptionType,
            solarEstimationType: estimationView.solarEstimationType,
            systemType: estimationView.systemType,
            inverterPhaseType: estimationView.inverterPhaseType,
            monthlyRequiredEnergy: inputParams.monthlyRequiredEnergy || 0,
            maxRequiredPower: inputParams.maxRequiredPower || 0,
            numberOfUsers: inputParams.numberOfUsers,
            demandPerUser: inputParams.demandPerUser,
            nightLoad: inputParams.nightLoadPercentage || 0,
            sunlightHours: inputParams.sunlightHours || 0,
            autonomyDays: inputParams.autonomyDays || null,
            systemVoltage: inputParams.systemVoltage || null,
            estimationSpaceClusterId:
              estimationView.solarEstimationType === EEstimationType.ADVANCED
                ? estimationView.spaceClusterId
                : null,
            estimationBasicScheduleId: null,
            isDraft: false,
            countryCode: estimationView.countryCode,
            installationCostRequested: includeOtherFees,
          })
            .unwrap()
            .then(() => {
              showSuccessMessage("Estimation saved successfully");
            })
            .catch((error: IHttpError) => {
              if (
                error.status === 412 &&
                error.ovstErrorCode === OvstErrorCode.OVST_CONS_0001
              ) {
                showErrorMessage("Please re-submit with address");
                return;
              }
              showErrorMessage("Failed to save estimation");
            });
        });
    }
  };

  const solarCategories = [
    EViewSolarEstimationSolarProductCategory.INVERTERS,
    EViewSolarEstimationSolarProductCategory.BATTERIES,
    EViewSolarEstimationSolarProductCategory.PANELS,
    EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS,
  ];

  const waterCategories = [
    EViewSolarEstimationSolarProductCategory.WATER_HEATERS,
    EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS,
  ];

  return (
    <>
      {isUserView ? (
        <Row className="bg-nav-bar align-items-center justify-content-between rounded-3 p-3 mx-0">
          <Col>
            <Row className="row-cols-auto align-items-center">
              <Col className="ms-2">
                <ArrowButton onBack={() => navigate(-1)} />
              </Col>
              <Col>
                <Row>
                  <Col className="title-2 font-weight-600 font-size-20">
                    {getHeaderName(
                      estimationInfo.estimationStatus as EstimationStatus,
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col className="text-advance-description font-weight-500 font-size-13">
                    Checkout form Chloride Exide
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
          <Col
            className={`col-auto ${
              estimationInfo.isDraft
                ? `text-primary status-pending-background`
                : getStatusColor(
                    estimationInfo.estimationStatus as EstimationStatus,
                  )
            } px-3 py-1 rounded-5 me-2`}
          >
            {estimationInfo.isDraft
              ? `Draft`
              : getStatusNameUser(
                  estimationInfo.estimationStatus as EstimationStatus,
                )}
          </Col>
        </Row>
      ) : (
        <Row className="bg-nav-bar border-2 border-color-2 align-items-center justify-content-between rounded-3 p-3 mx-0">
          <Col>
            <Row className="row-cols-auto gap-2 gap-lg-4">
              <Col className="ms-2">
                <ArrowButton onBack={() => navigate(AppRoute.ESTIMATIONS)} />
              </Col>
              <Col>
                <Row>
                  <Col className="text-advance-description font-weight-500 font-size-12">
                    Estimate No.
                  </Col>
                </Row>
                <Row>
                  <Col className="text-label font-weight-500 font-size-14">
                    {estimationInfo.estimationNo}
                  </Col>
                </Row>
              </Col>
              <Col>
                <Row>
                  <Col className="text-advance-description font-weight-500 font-size-12">
                    Created By
                  </Col>
                </Row>
                <Row>
                  <Col className="text-label font-weight-500 font-size-14">
                    {estimationInfo.createdBy}
                  </Col>
                </Row>
              </Col>
              <Col>
                <Row>
                  <Col className="text-advance-description font-weight-500 font-size-12">
                    Date
                  </Col>
                </Row>
                <Row>
                  <Col className="text-label font-weight-500 font-size-14">
                    {moment(estimationInfo.date).format("DD/MM/YYYY")}
                  </Col>
                </Row>
              </Col>
              <Col className="d-none d-lg-block">
                <Row>
                  <Col className="text-advance-description font-weight-500 font-size-12">
                    Email
                  </Col>
                </Row>
                <Row>
                  <Col className="text-label font-weight-500 font-size-14">
                    {estimationInfo.email}
                  </Col>
                </Row>
              </Col>
              <Col>
                <Row>
                  <Col className="text-advance-description font-weight-500 font-size-12">
                    Mobile Number
                  </Col>
                </Row>
                <Row>
                  <Col className="text-label font-weight-500 font-size-14">
                    {estimationInfo.mobileNumber}
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
          <Col className="col-auto">
            <StepsNumber
              steps={
                solarEstimationType === EEstimationType.DIRECT ||
                solarEstimationType === null
                  ? [steps[1]]
                  : steps
              }
              selectedStep={selectedStep}
              onStepClick={(selectedStep) => {
                setSelectedStep(selectedStep);
              }}
            />
          </Col>
        </Row>
      )}
      <div
        className={`estimation-container px-4 py-2 mt-3 rounded-3 ${styles.cardContainer}`}
      >
        <Row className="align-items-center justify-content-between">
          <Col className="col-auto">
            <Row>
              <Col className="text-color-3 font-size-14">
                #{estimationInfo.estimationNo}
              </Col>
            </Row>
            <Row>
              <Col className="title-2 font-weight-600 font-size-18">
                {estimationInfo.title}
              </Col>
            </Row>
          </Col>
          {solarSolutionType === ESolarSolutionType.BOTH ? (
            <Col className="col-12 mt-2">
              <Row className="align-items-stretch gx-2">
                <Col className="col-12 col-xxl-7">
                  <SelectedSolarSystemCard
                    monthlyEnergyNeed={monthlyEnergyNeed}
                    maxPower={maxPower}
                    dischargeRate={dischargeRate}
                    nightLoad={nightLoad}
                    sunlightHours={sunlightHours}
                    monthlyEnergyNeedFromUser={0}
                    maxPowerFromUser={0}
                    totalWattHours={selectedSystemOutput?.totalWattHours ?? 0}
                    maxPowerInWatt={selectedSystemOutput?.maxPowerInWatt ?? 0}
                    isHideEditIcon
                    solarSystemType={solarSystemType}
                    onChangeSolarSystemType={(option: Option) => {
                      setSolarSystemType(option);
                      setAutonomyDays(null);
                    }}
                    inverterPhaseType={inverterPhaseType}
                    onChangeInverterPhaseType={(option: Option) => {
                      setInverterPhaseType(option);
                    }}
                    systemVoltage={systemVoltage}
                    onSystemVoltageChange={(option: Option) => {
                      setSystemVoltage(option);
                    }}
                    autonomyDays={autonomyDays}
                    isHideDeleteIcon
                  />
                </Col>
                <Col className="col-12 col-xxl-5 mt-3 mt-xxl-0">
                  <SelectedWaterHeaterSystemCard
                    waterDemand={waterDemand}
                    totalLiters={selectedSystemOutput?.totalLiters || 0}
                    numberOfUsers={numberOfUsers}
                    demandPerUser={demandPerUser}
                    applicationType={applicationType}
                    utilityOption={utilityOption}
                    onApplicationTypeChange={(option: Option) => {
                      setApplicationType(option);
                    }}
                    onUtilityOptionChange={(option: Option) => {
                      setUtilityOption(option);
                    }}
                    isHideEditIcon
                    isHideDeleteIcon
                  />
                </Col>
              </Row>
            </Col>
          ) : solarSolutionType === ESolarSolutionType.SOLAR_ENERGY_SYSTEM ? (
            <Col className="col-12 col-xxl-auto mt-3 mt-xxl-0">
              <SelectedSolarSystemCard
                monthlyEnergyNeed={monthlyEnergyNeed}
                maxPower={maxPower}
                dischargeRate={dischargeRate}
                nightLoad={nightLoad}
                sunlightHours={sunlightHours}
                monthlyEnergyNeedFromUser={0}
                maxPowerFromUser={0}
                totalWattHours={selectedSystemOutput?.totalWattHours ?? 0}
                maxPowerInWatt={selectedSystemOutput?.maxPowerInWatt ?? 0}
                isHideEditIcon
                solarSystemType={solarSystemType}
                onChangeSolarSystemType={(option: Option) => {
                  setSolarSystemType(option);
                  setAutonomyDays(null);
                }}
                inverterPhaseType={inverterPhaseType}
                onChangeInverterPhaseType={(option: Option) => {
                  setInverterPhaseType(option);
                }}
                systemVoltage={systemVoltage}
                onSystemVoltageChange={(option: Option) => {
                  setSystemVoltage(option);
                }}
                autonomyDays={autonomyDays}
                isHideDeleteIcon
              />
            </Col>
          ) : (
            <Col className="col-12 col-xxl-auto mt-3 mt-xxl-0">
              <SelectedWaterHeaterSystemCard
                waterDemand={waterDemand}
                totalLiters={selectedSystemOutput?.totalLiters || 0}
                numberOfUsers={numberOfUsers}
                demandPerUser={demandPerUser}
                applicationType={applicationType}
                utilityOption={utilityOption}
                onApplicationTypeChange={(option: Option) => {
                  setApplicationType(option);
                }}
                onUtilityOptionChange={(option: Option) => {
                  setUtilityOption(option);
                }}
                isHideEditIcon
                isHideDeleteIcon
              />
            </Col>
          )}
          {/* {solarSolutionType === ESolarSolutionType.BOTH ? (
            <Col className="col-12 mt-2">
              <Row className="align-items-stretch gx-2">
                <Col className="col-12 col-xxl-7">
                  <SelectedSolarSystemCard
                    monthlyEnergyNeed={monthlyEnergyNeed}
                    maxPower={maxPower}
                    dischargeRate={dischargeRate}
                    nightLoad={nightLoad}
                    sunlightHours={sunlightHours}
                    monthlyEnergyNeedFromUser={0}
                    maxPowerFromUser={0}
                    totalWattHours={selectedSystemOutput?.totalWattHours ?? 0}
                    maxPowerInWatt={selectedSystemOutput?.maxPowerInWatt ?? 0}
                    isHideEditIcon
                    solarSystemType={solarSystemType}
                    setSolarSystemType={setSolarSystemType}
                  />
                </Col>
                <Col className="col-12 col-xxl-5 mt-3 mt-xxl-0">
                  <SelectedWaterHeaterSystemCard
                    waterDemand={waterDemand}
                    totalLiters={selectedSystemOutput?.totalLiters || 0}
                    numberOfUsers={numberOfUsers}
                    applicationType={applicationType}
                    utilityOption={utilityOption}
                    setApplicationType={setApplicationType}
                    setUtilityOption={setUtilityOption}
                    isHideEditIcon
                  />
                </Col>
              </Row>
            </Col>
          ) : solarSolutionType === ESolarSolutionType.SOLAR_ENERGY_SYSTEM ? (
            <Col className="col-12 col-xxl-auto mt-3 mt-xxl-0">
              <Row className="align-items-stretch gx-2">
                <Col>
                  <SelectedSolarSystemCard
                    monthlyEnergyNeed={monthlyEnergyNeed}
                    maxPower={maxPower}
                    dischargeRate={dischargeRate}
                    nightLoad={nightLoad}
                    sunlightHours={sunlightHours}
                    monthlyEnergyNeedFromUser={0}
                    maxPowerFromUser={0}
                    totalWattHours={selectedSystemOutput?.totalWattHours ?? 0}
                    maxPowerInWatt={selectedSystemOutput?.maxPowerInWatt ?? 0}
                    isHideEditIcon
                    solarSystemType={solarSystemType}
                    setSolarSystemType={setSolarSystemType}
                  />
                </Col>
                <Col className="col-12 col-xxl-auto mt-3 mt-xxl-0">
                  <IncludeSystemCard
                    icon="water_drop"
                    text="Include Solar Water Heating System as Well?"
                    onClick={() => console.log()}
                  />
                </Col>
              </Row>
            </Col>
          ) : (
            <Col className="col-12 col-xxl-auto mt-3 mt-xxl-0">
              <Row className="align-items-stretch gx-2">
                <Col>
                  <SelectedWaterHeaterSystemCard
                    waterDemand={waterDemand}
                    totalLiters={selectedSystemOutput?.totalLiters || 0}
                    numberOfUsers={numberOfUsers}
                    applicationType={applicationType}
                    utilityOption={utilityOption}
                    setApplicationType={setApplicationType}
                    setUtilityOption={setUtilityOption}
                    isHideEditIcon
                  />
                </Col>
                <Col className="col-12 col-xxl-auto mt-3 mt-xxl-0">
                  <IncludeSystemCard
                    icon="solar_power"
                    text="Include Solar Energy System as Well?"
                    onClick={() => console.log()}
                    textWidth="150px"
                  />
                </Col>
              </Row>
            </Col>
          )} */}
        </Row>
        {selectedStep === 1 ? (
          <Row>
            <Col>
              {solarEstimationType === EEstimationType.SIMPLIFIED ? (
                <AddUsageInfo
                  devices={devices}
                  setDevices={setDevices}
                  isHideAddDeviceButton={true}
                  disabled={!isUserView}
                />
              ) : (
                <BillCalculator
                  isHideAddScheduleButton={true}
                  isDisableCreatingOverlay={true}
                  isEnableDeletingSchedule={false}
                />
              )}
            </Col>
          </Row>
        ) : (
          <>
            {(solarSolutionType === ESolarSolutionType.SOLAR_ENERGY_SYSTEM ||
              solarSolutionType === ESolarSolutionType.BOTH) && (
              <>
                <Row className="my-2">
                  <Col className="text-secondary font-size-14 font-weight-500">
                    Solar Power Equipments
                  </Col>
                  <Col className="col-auto">
                    <MaterialIcon
                      icon={"keyboard_arrow_up"}
                      size={18}
                      color="var(--color-dropdown-more-icon)"
                      className={`${styles.iconToggle} ${
                        collapsed ? styles.isCollapsed : ""
                      } cursor-pointer`}
                      onClick={() => {
                        setCollapsed((ps) => !ps);
                      }}
                    />
                  </Col>
                </Row>
                <Collapse in={!collapsed}>
                  <div>
                    {userSelected.length > 0 &&
                      filterByCategories(userSelected, solarCategories)
                        .filter((s) => s.isSelected)
                        .map((selected) => {
                          return (
                            <Row key={selected.product.id} className="my-2">
                              <Col>
                                <ApprovedSolarComponent
                                  product={selected.product}
                                  count={selected.count}
                                />
                              </Col>
                            </Row>
                          );
                        })}
                  </div>
                </Collapse>
              </>
            )}
            {(solarSolutionType === ESolarSolutionType.BOTH ||
              solarSolutionType ===
                ESolarSolutionType.WATER_HEATING_SYSTEM) && (
              <>
                <Row className="mt-3 mb-2">
                  <Col className="text-secondary font-size-14 font-weight-500">
                    Water Heater Equipments
                  </Col>
                  <Col className="col-auto">
                    <MaterialIcon
                      icon={"keyboard_arrow_up"}
                      size={18}
                      color="var(--color-dropdown-more-icon)"
                      className={`${styles.iconToggle} ${
                        waterHeaterCollapsed ? styles.isCollapsed : ""
                      } cursor-pointer`}
                      onClick={() => {
                        setWaterHeaterCollapsed((ps) => !ps);
                      }}
                    />
                  </Col>
                </Row>
                <Collapse in={!waterHeaterCollapsed}>
                  <div>
                    {userSelected.length > 0 &&
                      filterByCategories(userSelected, waterCategories).map(
                        (selected) => {
                          return (
                            <Row key={selected.product.id} className="my-2">
                              <Col>
                                <ApprovedSolarComponent
                                  product={selected.product}
                                  count={selected.count}
                                />
                              </Col>
                            </Row>
                          );
                        },
                      )}
                  </div>
                </Collapse>
              </>
            )}
          </>
        )}
        {selectedStep === 2 && (
          <>
            <Row
              className={`my-5 ${
                isUserView
                  ? `justify-content-between align-items-center`
                  : `justify-content-between align-items-start`
              }`}
            >
              {!isUserView && (
                <Col className="col-12 col-lg-5 mb-4 mb-lg-0">
                  <SiteLocationCard address={estimationInfo.address} />
                </Col>
              )}
              {isUserView && (
                <Col className="col-6">
                  <Row className="align-items-center">
                    <Col className="col-auto">
                      <AppSwitch
                        isOn={includeOtherFees}
                        size="sm"
                        onSwitch={(value) => {
                          if (
                            estimationInfo.estimationStatus !==
                              EstimationStatus.APPROVED &&
                            estimationInfo.estimationStatus !==
                              EstimationStatus.APPROVED_WITH_CHANGES
                          ) {
                            setIncludeOtherFees(value);
                          }
                        }}
                        disabled={
                          estimationInfo.estimationStatus ===
                            EstimationStatus.APPROVED ||
                          estimationInfo.estimationStatus ===
                            EstimationStatus.APPROVED_WITH_CHANGES ||
                          estimationInfo.estimationStatus ===
                            EstimationStatus.PURCHASED ||
                          estimationInfo.estimationStatus ===
                            EstimationStatus.SUBMITTED ||
                          estimationInfo.estimationStatus ===
                            EstimationStatus.MODIFIED ||
                          !assembleSystemData ||
                          (!assembleSystemData.userSelectedProducts &&
                            !assembleSystemData.adminSelectedProducts)
                        }
                      />
                    </Col>
                    <Col>
                      <Row>
                        <Col className="text-label font-weight-500 font-size-16">
                          Include Installation and Accessory Fees
                        </Col>
                      </Row>
                      <Row>
                        <Col className="text-advance-description font-weight-400 font-size-12">
                          If selected Solar system installation will be handled
                          by Chloride Exide experts & installation cost will be
                          included into the bill
                        </Col>
                      </Row>
                      <Row className="mx-0 mt-1">
                        {estimationInfo.estimationStatus ===
                          EstimationStatus.APPROVED_WITH_CHANGES ||
                        estimationInfo.estimationStatus ===
                          EstimationStatus.APPROVED ? (
                          <Col className="font-size-12 font-weight-400 status-background status-approved px-3 py-1 rounded-5 col-auto">
                            {estimationInfo.estimationStatus ===
                            EstimationStatus.APPROVED_WITH_CHANGES
                              ? `Surveyed and Approved with Changes by Experts`
                              : `Surveyed and Approved by Experts`}
                          </Col>
                        ) : (
                          <Col className="text-brown font-size-12 bg-background-15 px-4 py-2 rounded-3">
                            Installation & Accessories cost subject to change
                            based on survey by experts. Shown values are
                            approximate only
                          </Col>
                        )}
                      </Row>
                    </Col>
                  </Row>
                </Col>
              )}
              <Col className="text-end">
                <Row>
                  <Col className="text-advance-description font-size-14">
                    Components Cost
                  </Col>
                  <Col xl={4} className="title-2 font-weight-500">
                    {currency}&nbsp;
                    {formatWithCommas(Number(Helper.roundTo2(componentCost)))}
                  </Col>
                </Row>
                {includeOtherFees && (
                  <Row>
                    <Col className="text-advance-description font-size-14">
                      {estimationInfo.estimationStatus ===
                        EstimationStatus.APPROVED ||
                      estimationInfo.estimationStatus ===
                        EstimationStatus.APPROVED_WITH_CHANGES
                        ? `Installation Cost`
                        : `Approximate Installation Cost`}
                    </Col>
                    <Col xl={4} className="title-2 font-weight-500">
                      {currency}&nbsp;
                      {formatWithCommas(
                        Number(Helper.roundTo2(installationCost)),
                      )}
                    </Col>
                  </Row>
                )}
                {includeOtherFees && (
                  <Row>
                    <Col className="text-advance-description font-size-14">
                      {estimationInfo.estimationStatus ===
                        EstimationStatus.APPROVED ||
                      estimationInfo.estimationStatus ===
                        EstimationStatus.APPROVED_WITH_CHANGES
                        ? `Accessories Cost`
                        : `Approximate Accessories Cost`}
                    </Col>
                    <Col xl={4} className="title-2 font-weight-500">
                      {currency}&nbsp;
                      {formatWithCommas(
                        Number(Helper.roundTo2(accessoriesCost)),
                      )}
                    </Col>
                  </Row>
                )}
                <Row className="mt-2">
                  <Col className="text-advance-description">
                    {estimationInfo.estimationStatus ===
                      EstimationStatus.APPROVED ||
                    estimationInfo.estimationStatus ===
                      EstimationStatus.APPROVED_WITH_CHANGES ? (
                      "Total"
                    ) : (
                      <>{includeOtherFees ? `Approximate` : ``}&nbsp;Total</>
                    )}
                  </Col>
                  <Col xl={4}>
                    <Row>
                      <Col className="title-2 font-size-20 font-weight-600">
                        {currency}&nbsp;
                        {formatWithCommas(Number(Helper.roundTo2(totalCost)))}
                      </Col>
                    </Row>
                    <Row className="text-advance-description font-weight-400 font-size-12">
                      <Col>Tax included</Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className="align-items-center justify-content-between mb-4">
              <Col className="col-auto">
                <button
                  className={`bg-background-6 border-0 rounded-3 d-flex align-items-center justify-content-center py-2 px-3 ${
                    !assembleSystemData ||
                    (!assembleSystemData.userSelectedProducts &&
                      !assembleSystemData.adminSelectedProducts)
                      ? "cursor-disabled"
                      : "cursor-pointer"
                  }`}
                  onClick={() => setShowUpdateHistoryModal(true)}
                  disabled={
                    !assembleSystemData ||
                    (!assembleSystemData.userSelectedProducts &&
                      !assembleSystemData.adminSelectedProducts)
                  }
                >
                  <Row className="align-items-center">
                    <Col className="col-auto pe-0">
                      <div className={styles.updateHistoryIconWrapper}>
                        <MaterialIcon
                          size={16}
                          icon="receipt_long"
                          color="var(--color-receipt-icon)"
                          className="bg-background-5 p-1 rounded-circle"
                        />
                        {comment && (
                          <div className={styles.updateHistoryCount}>1</div>
                        )}
                      </div>
                    </Col>
                    <Col className="title-4 font-weight-500 font-size-13">
                      Update History
                    </Col>
                  </Row>
                </button>
              </Col>
              <Col className="col-auto">
                {isUserView ? (
                  <>
                    {estimationInfo.estimationStatus ===
                    EstimationStatus.REJECTED ? (
                      <Row className="gx-3">
                        <Col className="col-auto">
                          <AppButton
                            text="Update System"
                            className="px-4"
                            style={{ height: "40px", borderRadius: "8px" }}
                            onClick={() => {
                              if (
                                solarEstimationType ===
                                EEstimationType.SIMPLIFIED
                              ) {
                                localStorage.setItem(
                                  ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
                                  JSON.stringify({
                                    estimationId: estimationId,
                                    estimationToken: "token",
                                  }),
                                );

                                navigate(
                                  AppRoute.DESIGN_SYSTEM_SIMPLIFIED_ASSEMBLE_SYSTEM,
                                );
                                return;
                              }

                              if (
                                solarEstimationType === EEstimationType.DIRECT
                              ) {
                                localStorage.setItem(
                                  ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
                                  JSON.stringify({
                                    estimationId: estimationId,
                                    estimationToken: "token",
                                  }),
                                );

                                navigate(
                                  AppRoute.DESIGN_SYSTEM_DIRECT_ASSEMBLE_SYSTEM,
                                );
                                return;
                              }

                              if (
                                solarEstimationType === EEstimationType.ADVANCED
                              ) {
                                localStorage.setItem(
                                  ESolarSimLocalStorage.ESTIMATOR_DETAILS_ADVANCED,
                                  JSON.stringify({
                                    estimationId: estimationId,
                                    estimationToken: "token",
                                  }),
                                );

                                navigate(
                                  AppRoute.DESIGN_SYSTEM_ADVANCED_DEVICES,
                                  {
                                    state: {
                                      isNavigateToAssembleSystem: true,
                                    },
                                  },
                                );
                                return;
                              }

                              localStorage.setItem(
                                ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
                                JSON.stringify({
                                  estimationId: estimationId,
                                  estimationToken: "token",
                                }),
                              );

                              navigate(
                                AppRoute.DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM,
                              );
                            }}
                          />
                        </Col>
                      </Row>
                    ) : (
                      <Row className="gx-3">
                        {estimationInfo.estimationStatus !==
                          EstimationStatus.APPROVED &&
                          estimationInfo.estimationStatus !==
                            EstimationStatus.APPROVED_WITH_CHANGES &&
                          estimationInfo.estimationStatus !==
                            EstimationStatus.PURCHASED && (
                            <>
                              <Col className="col-auto">
                                <button
                                  className={`bg-background-16 border-0 rounded-2 d-flex align-items-center justify-content-center py-2 px-3 ${
                                    styles.button
                                  } ${
                                    estimationInfo.estimationStatus ===
                                      EstimationStatus.SUBMITTED ||
                                    estimationInfo.estimationStatus ===
                                      EstimationStatus.MODIFIED ||
                                    !assembleSystemData ||
                                    (!assembleSystemData.userSelectedProducts &&
                                      !assembleSystemData.adminSelectedProducts)
                                      ? "opacity-50 cursor-disabled"
                                      : "cursor-pointer"
                                  }`}
                                  onClick={onSave}
                                  disabled={
                                    estimationInfo.estimationStatus ===
                                      EstimationStatus.SUBMITTED ||
                                    estimationInfo.estimationStatus ===
                                      EstimationStatus.MODIFIED ||
                                    !assembleSystemData ||
                                    (!assembleSystemData.userSelectedProducts &&
                                      !assembleSystemData.adminSelectedProducts)
                                  }
                                >
                                  <Row className="align-items-center">
                                    <Col className="col-auto pe-0">
                                      <MaterialIcon
                                        size={16}
                                        icon="save"
                                        color="#384F82"
                                      />
                                    </Col>
                                    <Col className="text-primary font-weight-600 font-size-16">
                                      Save
                                    </Col>
                                  </Row>
                                </button>
                              </Col>
                              <Col className="col-auto">
                                <button
                                  className={`bg-background-16 rounded-2 border-0 d-flex align-items-center justify-content-center py-2 px-3 ${
                                    styles.button
                                  } ${
                                    estimationInfo.estimationStatus ===
                                      EstimationStatus.SUBMITTED ||
                                    estimationInfo.estimationStatus ===
                                      EstimationStatus.MODIFIED ||
                                    !assembleSystemData ||
                                    (!assembleSystemData.userSelectedProducts &&
                                      !assembleSystemData.adminSelectedProducts)
                                      ? "opacity-50 cursor-disabled"
                                      : "cursor-pointer"
                                  }`}
                                  onClick={() => {
                                    setIsShowSubmitForExpertApprovalModal(true);
                                  }}
                                  disabled={
                                    estimationInfo.estimationStatus ===
                                      EstimationStatus.SUBMITTED ||
                                    estimationInfo.estimationStatus ===
                                      EstimationStatus.MODIFIED ||
                                    !assembleSystemData ||
                                    (!assembleSystemData.userSelectedProducts &&
                                      !assembleSystemData.adminSelectedProducts)
                                  }
                                >
                                  <Row className="align-items-center">
                                    <Col className="col-auto pe-0">
                                      <MaterialIcon
                                        size={16}
                                        icon="ungroup"
                                        color="#384F82"
                                      />
                                    </Col>
                                    <Col className="text-primary font-weight-600 font-size-16">
                                      Submit for Expert Approval
                                    </Col>
                                  </Row>
                                </button>
                              </Col>
                            </>
                          )}
                        {(estimationInfo.estimationStatus ===
                          EstimationStatus.APPROVED ||
                          estimationInfo.estimationStatus ===
                            EstimationStatus.APPROVED_WITH_CHANGES) && (
                          <Col className="col-auto">
                            <button
                              className="bg-background-16 border border-2 border-blue-5 rounded-2 d-flex align-items-center justify-content-center py-2 px-3 cursor-pointer"
                              onClick={() => {
                                if (
                                  solarEstimationType ===
                                  EEstimationType.SIMPLIFIED
                                ) {
                                  localStorage.setItem(
                                    ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
                                    JSON.stringify({
                                      estimationId: estimationId,
                                      estimationToken: "token",
                                    }),
                                  );

                                  navigate(
                                    AppRoute.DESIGN_SYSTEM_SIMPLIFIED_ASSEMBLE_SYSTEM,
                                  );
                                  return;
                                }

                                if (
                                  solarEstimationType === EEstimationType.DIRECT
                                ) {
                                  localStorage.setItem(
                                    ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
                                    JSON.stringify({
                                      estimationId: estimationId,
                                      estimationToken: "token",
                                    }),
                                  );

                                  navigate(
                                    AppRoute.DESIGN_SYSTEM_DIRECT_ASSEMBLE_SYSTEM,
                                  );
                                  return;
                                }

                                if (
                                  solarEstimationType ===
                                  EEstimationType.ADVANCED
                                ) {
                                  localStorage.setItem(
                                    ESolarSimLocalStorage.ESTIMATOR_DETAILS_ADVANCED,
                                    JSON.stringify({
                                      estimationId: estimationId,
                                      estimationToken: "token",
                                    }),
                                  );

                                  navigate(
                                    AppRoute.DESIGN_SYSTEM_ADVANCED_DEVICES,
                                    {
                                      state: {
                                        isNavigateToAssembleSystem: true,
                                      },
                                    },
                                  );
                                  return;
                                }

                                localStorage.setItem(
                                  ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
                                  JSON.stringify({
                                    estimationId: estimationId,
                                    estimationToken: "token",
                                  }),
                                );

                                navigate(
                                  AppRoute.DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM,
                                );
                              }}
                            >
                              <Row className="align-items-center">
                                <Col className="col-auto pe-0">
                                  <MaterialIcon
                                    size={16}
                                    icon="border_color"
                                    color="#384F82"
                                  />
                                </Col>
                                <Col className="text-primary font-weight-600 font-size-16">
                                  Update System
                                </Col>
                              </Row>
                            </button>
                          </Col>
                        )}
                        <Col className="col-auto">
                          <AppButton
                            text="Purchase System"
                            iconName="shopping_cart"
                            iconColor="#fff"
                            className="px-4"
                            style={{ height: "40px", borderRadius: "8px" }}
                            // disabled={
                            //   (includeOtherFees &&
                            //     estimationInfo.estimationStatus !==
                            //       EstimationStatus.APPROVED &&
                            //     estimationInfo.estimationStatus !==
                            //       EstimationStatus.APPROVED_WITH_CHANGES) ||
                            //   !assembleSystemData ||
                            //   (!assembleSystemData.userSelectedProducts &&
                            //     !assembleSystemData.adminSelectedProducts) ||
                            //   estimationInfo.estimationStatus ===
                            //     EstimationStatus.PURCHASED ||
                            //   estimationInfo.estimationStatus ===
                            //     EstimationStatus.SUBMITTED ||
                            //   estimationInfo.estimationStatus ===
                            //     EstimationStatus.MODIFIED
                            // }
                            disabled={true}
                            onClick={() => onPurchaseSystem(estimationId)}
                            isLoading={isLoadingPurchaseSolarEstimation}
                          />
                        </Col>
                      </Row>
                    )}
                  </>
                ) : (
                  <>
                    {![
                      EstimationStatus.APPROVED,
                      EstimationStatus.APPROVED_WITH_CHANGES,
                      EstimationStatus.REJECTED,
                      EstimationStatus.PURCHASED,
                    ].includes(
                      estimationInfo.estimationStatus as EstimationStatus,
                    ) && (
                      <Row className="gx-3">
                        <Col className="col-auto">
                          <button
                            className="bg-background-16 border border-2 border-blue-5 rounded-2 d-flex align-items-center justify-content-center py-2 px-3 cursor-pointer"
                            onClick={() =>
                              navigate(
                                AppRoute.EDIT_ESTIMATIONS + "/" + estimationId,
                                {
                                  state: {
                                    estimationId,
                                  },
                                },
                              )
                            }
                          >
                            <Row className="align-items-center">
                              <Col className="col-auto pe-0">
                                <MaterialIcon
                                  size={16}
                                  icon="border_color"
                                  color="#384F82"
                                />
                              </Col>
                              <Col className="text-primary font-weight-600 font-size-16">
                                Edit System
                              </Col>
                            </Row>
                          </button>
                        </Col>
                        <Col className="col-auto">
                          <button
                            className="bg-primary border-0 rounded-2 d-flex align-items-center justify-content-center py-2 px-3 cursor-pointer"
                            onClick={() => setShowApproveSystemModal(true)}
                          >
                            <Row className="align-items-center">
                              <Col className="col-auto pe-0">
                                <MaterialIcon
                                  size={16}
                                  icon="check_circle"
                                  color="#FFFFFF"
                                />
                              </Col>
                              <Col className="text-white font-weight-600 font-size-16">
                                {selectedStatus.label}
                              </Col>
                              <Col className="ps-0">
                                <AppDropDown
                                  items={[
                                    {
                                      text: "Approve",
                                      onClick: () =>
                                        setSelectedStatus({
                                          value: EstimationStatus.APPROVED,
                                          label: "Approve",
                                        }),
                                    },
                                    {
                                      text: "Reject",
                                      onClick: () =>
                                        setSelectedStatus({
                                          value: EstimationStatus.REJECTED,
                                          label: "Reject",
                                        }),
                                    },
                                  ]}
                                  icon="arrow_drop_down"
                                  showBackgroundAndBorder={false}
                                />
                              </Col>
                            </Row>
                          </button>
                        </Col>
                      </Row>
                    )}
                  </>
                )}
              </Col>
            </Row>
          </>
        )}
      </div>
      <UpdateHistoryModal
        show={showUpdateHistoryModal}
        onClose={() => setShowUpdateHistoryModal(false)}
        estimationId={estimationId}
        user={estimationInfo.createdBy}
        currency={estimationInfo.currency}
        estimationNo={estimationInfo.estimationNo}
        isUserView={isUserView}
      />
      <ApproveSystemModal
        show={showApproveSystemModal}
        onClose={() => setShowApproveSystemModal(false)}
        onCancel={() => setShowApproveSystemModal(false)}
        estimationId={estimationId}
        user={estimationInfo.createdBy}
        selectedStatus={selectedStatus.value as EstimationStatus}
        currency={estimationInfo.currency}
        isAdminModify={!!assembleSystemData?.adminSelectedProducts}
        onShowPurchaseHistory={() => setShowPurchaseHistoryModal(true)}
        setIsUpdateCurrentSate={setIsUpdateCurrentSate}
      />
      <PurchaseHistoryModal
        show={showPurchaseHistoryModal}
        onClose={() => setShowPurchaseHistoryModal(false)}
        userId={estimationInfo.userId}
      />
      <SubmitForExpertApprovalModal
        show={isShowSubmitForExpertApprovalModal}
        onCancel={() => {
          setIsShowSubmitForExpertApprovalModal(false);
        }}
        onClose={() => {
          setIsShowSubmitForExpertApprovalModal(false);
        }}
        onConfirm={(
          address: string,
          longitude: number,
          latitude: number,
          note: string,
        ) => {
          onSubmitForApproval(address, longitude, latitude, note);
        }}
        includeOtherFees={includeOtherFees}
      />
      <SpinnerModal
        show={
          isFetchingViewSolarEstimationAdmin ||
          isFetchingViewEstimation ||
          isSubmittingForApproval ||
          isLoadingCalculateSolarEstimationCost ||
          isLoadingCalculateSolarEstimationCostAdmin ||
          isLoadingPersistEstimation ||
          isLoadingUpdateEstimation
        }
      />
    </>
  );
};

export default ViewEstimation;
